import { useEffect, useId, useRef, useState } from "react"
import type { PortalRequestResponse } from "@ledgerbox/domain"

import { usePortalApi } from "./api-context"
import { InlineError, ModalDialog } from "./ui"

type Item = PortalRequestResponse["items"][number]

/**
 * What the firm attached to show what it wants, under the item's
 * description: an image in place that opens larger, or a file to download.
 * An image's short-lived link is read when the item shows; a file's is read
 * on the tap.
 */
export function ItemExample({ item }: { item: Item }) {
  if (!item.example) return null
  return item.example.kind === "image" ? (
    <ExampleImage item={item} />
  ) : (
    <ExampleDownload item={item} />
  )
}

function ExampleImage({ item }: { item: Item }) {
  const api = usePortalApi()
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const [open, setOpen] = useState(false)
  const closeButton = useRef<HTMLButtonElement>(null)
  const titleId = useId()

  useEffect(() => {
    let active = true
    void api
      .getItemExample(item.itemId)
      .then((access) => {
        if (active) setUrl(access.accessUrl)
      })
      .catch(() => {
        if (active) setFailed(true)
      })
    return () => {
      active = false
    }
  }, [api, item.itemId])

  if (failed)
    return <p className="item-example-note">The example could not be shown.</p>

  return (
    <figure className="item-example">
      {url ? (
        <button
          type="button"
          className="item-example-thumb"
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
        >
          <img src={url} alt={`Example for ${item.name}. Open it larger`} />
        </button>
      ) : (
        <span className="item-example-thumb is-loading" aria-hidden="true" />
      )}
      <figcaption>An example of what to send</figcaption>
      {url ? (
        <ModalDialog
          open={open}
          onClose={() => setOpen(false)}
          labelledBy={titleId}
          className="example-dialog"
          initialFocus={closeButton}
        >
          <div className="example-dialog-head">
            <h2 id={titleId}>Example for {item.name}</h2>
            <button
              ref={closeButton}
              type="button"
              className="icon-button"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <img
            className="example-dialog-image"
            src={url}
            alt={`Example for ${item.name}`}
          />
        </ModalDialog>
      ) : null}
    </figure>
  )
}

function ExampleDownload({ item }: { item: Item }) {
  const api = usePortalApi()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The link is read on the tap: it lasts minutes and the page may stay
  // open longer. The storage answers it as an attachment under the file's
  // own name, so going to it saves the file and the page stays. A new tab
  // opened after the wait is a popup Safari blocks (R4-F1).
  const download = async () => {
    setPending(true)
    setError(null)
    try {
      const access = await api.getItemExample(item.itemId)
      window.location.assign(access.accessUrl)
    } catch {
      setError("The example could not be opened. Try again.")
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="item-example item-example-file">
      <button
        type="button"
        className="item-example-download"
        disabled={pending}
        onClick={() => void download()}
      >
        Download the example
      </button>
      {error ? <InlineError message={error} /> : null}
    </div>
  )
}
